import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import {
  altTextFor, focalPosition, homeEntry, isFutureBoardBuildEnabled, listFutureCards, parseFutureBoardError,
  type FutureCardList,
} from "@chronospark/shared";
import { outlinedButtonSx } from "./buttonStyles";

type Translate = (key: string, options?: object) => string;

const BOARD_PATH = "/pages/ai-coaching/FutureBoard";

// ホーム（ダッシュボード）の入口。今日の一枚があればその画像と見出し、なければ未来ボードへの入口。
// 未来ボードが使えない間（ビルドで無効・サーバーで無効・読み込みの失敗）は、何も表示せず、ホームの邪魔をしない。
function TodayEntry() {
  const enabled = isFutureBoardBuildEnabled(import.meta.env.VITE_FUTURE_BOARD_ENABLED);
  const { accessToken } = useSessionContext() as { accessToken?: string };
  const { t, language } = useContext(LanguageContext) as { t: Translate; language: string };
  const navigate = useNavigate();
  const [list, setList] = useState<FutureCardList | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!enabled || !accessToken) return undefined;
    let cancelled = false;
    listFutureCards(accessToken, "visible", { hideGlobalLoader: true })
      .then(result => { if (!cancelled) setList(result); })
      .catch(error => {
        // 無効化（FEATURE_DISABLED）も、通信の失敗も、ホームには何も出さない
        void parseFutureBoardError(error);
        if (!cancelled) setHidden(true);
      });
    return () => { cancelled = true; };
  }, [enabled, accessToken]);

  if (!enabled || hidden || !list) return null;

  const entry = homeEntry(list);

  return (
    <Box component="section" aria-label={t("futureBoard.title")} sx={{ mt: 2, mb: 1 }}>
      {entry.kind === "pinned" ? (
        <Card>
          <CardActionArea
            onClick={() => navigate(`${BOARD_PATH}?card=${entry.card.id}`)}
            aria-label={`${t("futureBoard.list.openScene")}: ${entry.card.title}`}
          >
            <Stack direction={{ xs: "column", sm: "row" }}>
              {entry.card.cover?.thumb_url && (
                <Box
                  component="img"
                  src={entry.card.cover.thumb_url}
                  alt={altTextFor(entry.card.cover, language, t("futureBoard.image.defaultAlt"), entry.card.cover.alt_text_en)}
                  sx={{ width: { xs: "100%", sm: 240 }, height: { xs: 160, sm: "auto" }, objectFit: "cover", objectPosition: focalPosition(entry.card.cover) }}
                />
              )}
              <Box sx={{ p: 2 }}>
                <Chip size="small" color="info" label={t("futureBoard.list.today")} sx={{ mb: 1 }} />
                <Typography variant="h5" component="h2" sx={{ overflowWrap: "anywhere" }}>{entry.card.title}</Typography>
                {entry.card.meaning_excerpt && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{entry.card.meaning_excerpt}</Typography>}
                <Typography variant="button" color="info.main" sx={{ display: "block", mt: 1 }}>{t("futureBoard.list.openScene")}</Typography>
              </Box>
            </Stack>
          </CardActionArea>
        </Card>
      ) : (
        <Card>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between" sx={{ p: 2 }}>
            <Box>
              <Typography variant="h6" component="h2">{t("futureBoard.title")}</Typography>
              <Typography variant="body2" color="text.secondary">{t("futureBoard.list.noTodayHint")}</Typography>
            </Box>
            <Button variant="outlined" sx={outlinedButtonSx} onClick={() => navigate(BOARD_PATH)} startIcon={<Icon>auto_awesome</Icon>}>
              {t("futureBoard.home.openBoard")}
            </Button>
          </Stack>
        </Card>
      )}
    </Box>
  );
}

export default TodayEntry;
